import { useState } from 'react';
import { Banknote, CreditCard, Building2, CircleDot, Globe } from 'lucide-react';
import { MetricCard } from '@/components/reports/MetricCard';
import { PaymentMethodTotals } from '@/types/finance';
import { PaymentMethodDetailsDialog } from './PaymentMethodDetailsDialog';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface PaymentMethodsPanelProps {
  data: PaymentMethodTotals | null;
  isLoading: boolean;
  startDate: Date;
  endDate: Date;
  timezone: string;
}

export const PaymentMethodsPanel = ({
  data,
  isLoading,
  startDate,
  endDate,
  timezone
}: PaymentMethodsPanelProps) => {
  const { formatMoney: formatCurrency } = useCompanyLocale();
  const [selectedMethod, setSelectedMethod] = useState<{ id: string, title: string } | null>(null);

  const handleMethodClick = (id: string, title: string) => {
    if (!data) return;
    setSelectedMethod({ id, title });
  };

  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  const totalCollected = data.cash + data.card + data.bank_transfer + (data.square ?? 0) + data.other;

  const getPercentage = (value: number) => {
    if (totalCollected === 0) return '0%';
    return `${((value / totalCollected) * 100).toFixed(1)}%`;
  };

  return (
    <>
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-muted-foreground">Payment Methods Breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <MetricCard
            title="Cash Received"
            value={formatCurrency(data.cash)}
            subtitle={getPercentage(data.cash)}
            icon={Banknote}
            iconBgColor="bg-success/10"
            iconColor="text-success"
            valueColor="text-success"
            onClick={() => handleMethodClick('cash', 'Cash')}
            tooltip="Click to view jobs"
          />
          <MetricCard
            title="Card/EFTPOS"
            value={formatCurrency(data.card)}
            subtitle={getPercentage(data.card)}
            icon={CreditCard}
            iconBgColor="bg-info/10"
            iconColor="text-info"
            valueColor="text-info"
            onClick={() => handleMethodClick('card', 'Card/EFTPOS')}
            tooltip="Click to view jobs"
          />
          <MetricCard
            title="Bank Transfer"
            value={formatCurrency(data.bank_transfer)}
            subtitle={getPercentage(data.bank_transfer)}
            icon={Building2}
            iconBgColor="bg-primary/10"
            iconColor="text-primary"
            valueColor="text-primary"
            onClick={() => handleMethodClick('bank_transfer', 'Bank Transfer')}
            tooltip="Click to view jobs"
          />
          <MetricCard
            title="Square (online)"
            value={formatCurrency(data.square ?? 0)}
            subtitle={getPercentage(data.square ?? 0)}
            icon={Globe}
            iconBgColor="bg-info/10"
            iconColor="text-info"
            valueColor="text-info"
            onClick={() => handleMethodClick('square', 'Square (online)')}
            tooltip="Click to view jobs"
          />
          <MetricCard
            title="Other"
            value={formatCurrency(data.other)}
            subtitle={getPercentage(data.other)}
            icon={CircleDot}
            iconBgColor="bg-muted"
            iconColor="text-muted-foreground"
            onClick={() => handleMethodClick('other', 'Other')}
            tooltip="Click to view jobs"
          />
        </div>
      </div>

      <PaymentMethodDetailsDialog
        isOpen={!!selectedMethod}
        onClose={() => setSelectedMethod(null)}
        title={selectedMethod?.title || ''}
        paymentMethod={selectedMethod?.id || null}
        startDate={startDate}
        endDate={endDate}
        timezone={timezone}
      />
    </>
  );
};
